/**
 * Data Room Store
 *
 * Zustand store for managing data room document uploads and progress.
 * Supports document bundles that cover multiple checklist items.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getStorage } from '@/lib/state/ssr-storage';
import type { DocumentStatus, DocumentUpload, FundingStage } from './types';
import type { StartupDomain } from './domains';
import type { UploadedDocument, UploadedDocumentType } from './document-mapping';
import { getChecklistItemsCovered } from './document-mapping';
import type { StorageFile } from '@/lib/storage';

interface DataRoomState {
  // Document upload status (individual items)
  uploads: Record<string, DocumentUpload>;

  // Uploaded documents (bundles that cover multiple items)
  uploadedDocuments: UploadedDocument[];

  // Current stage for filtering
  currentStage: FundingStage;

  // Startup domain for domain-specific requirements
  domain: StartupDomain;

  // Domain-specific document status
  domainUploads: Record<string, DocumentUpload>;

  // Storage file references
  storageFiles: Record<string, StorageFile>;

  // ISO timestamp of the last successful cloud sync (persisted UX metadata)
  lastSyncedAt: string | null;

  // Actions
  setLastSyncedAt: (iso: string | null) => void;
  setDocumentStatus: (
    documentId: string,
    status: DocumentStatus,
    metadata?: Partial<DocumentUpload>
  ) => void;
  setCurrentStage: (stage: FundingStage) => void;
  setDomain: (domain: StartupDomain) => void;

  // Upload a document bundle that covers multiple items
  uploadDocumentBundle: (
    id: string,
    fileName: string,
    documentType: UploadedDocumentType,
    customCoversItems?: string[],
    fileUrl?: string
  ) => void;

  // Remove an uploaded document bundle
  removeDocumentBundle: (id: string) => void;

  // Mark individual item status
  markAsUploaded: (documentId: string, fileName: string, fileUrl?: string) => void;
  markAsVerified: (documentId: string) => void;
  markAsNotApplicable: (documentId: string) => void;
  addNote: (documentId: string, note: string) => void;
  resetDocument: (documentId: string) => void;
  resetAll: () => void;

  // Domain-specific document actions
  setDomainDocumentStatus: (
    documentId: string,
    status: DocumentStatus,
    metadata?: Partial<DocumentUpload>
  ) => void;

  // Storage file actions
  addStorageFile: (documentId: string, file: StorageFile) => void;
  removeStorageFile: (documentId: string) => void;
  getStorageFile: (documentId: string) => StorageFile | undefined;

  // Computed helpers
  getUpload: (documentId: string) => DocumentUpload | undefined;
  getUploadStatus: (documentId: string) => DocumentStatus;
  isItemCovered: (documentId: string) => boolean;
  getCoveringDocument: (documentId: string) => UploadedDocument | undefined;
}

const initialState = {
  uploads: {} as Record<string, DocumentUpload>,
  uploadedDocuments: [] as UploadedDocument[],
  currentStage: 'seed' as FundingStage,
  domain: 'saas' as StartupDomain,
  domainUploads: {} as Record<string, DocumentUpload>,
  storageFiles: {} as Record<string, StorageFile>,
  lastSyncedAt: null as string | null,
};

export const useDataRoomStore = create<DataRoomState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setDocumentStatus: (documentId, status, metadata) => {
        set((state) => ({
          uploads: {
            ...state.uploads,
            [documentId]: {
              ...state.uploads[documentId],
              ...metadata,
              documentId,
              status,
            },
          },
        }));
      },

      setCurrentStage: (stage) => {
        set({ currentStage: stage });
      },

      setDomain: (domain) => {
        set({ domain });
      },

      uploadDocumentBundle: (id, fileName, documentType, customCoversItems, fileUrl) => {
        const coversItems = customCoversItems || getChecklistItemsCovered(documentType);

        const newDoc: UploadedDocument = {
          id,
          fileName,
          fileUrl,
          uploadedAt: new Date().toISOString(),
          documentType,
          coversItems,
        };

        set((state) => {
          // Mark all covered items as uploaded
          const updatedUploads = { ...state.uploads };
          for (const itemId of coversItems) {
            updatedUploads[itemId] = {
              ...updatedUploads[itemId],
              documentId: itemId,
              status: 'uploaded',
              fileName,
              fileUrl,
              uploadedAt: newDoc.uploadedAt,
            };
          }

          return {
            uploadedDocuments: [...state.uploadedDocuments, newDoc],
            uploads: updatedUploads,
          };
        });
      },

      removeDocumentBundle: (id) => {
        set((state) => {
          const docToRemove = state.uploadedDocuments.find((d) => d.id === id);
          if (!docToRemove) return state;

          // Reset status for items only covered by this document
          const updatedUploads = { ...state.uploads };
          const remainingDocs = state.uploadedDocuments.filter((d) => d.id !== id);

          for (const itemId of docToRemove.coversItems) {
            // Check if any other document covers this item
            const stillCovered = remainingDocs.some((d) => d.coversItems.includes(itemId));
            if (!stillCovered) {
              // Only reset if not covered by another document
              const upload = updatedUploads[itemId];
              if (upload && upload.fileName === docToRemove.fileName) {
                updatedUploads[itemId] = {
                  documentId: itemId,
                  status: 'not-started',
                };
              }
            }
          }

          return {
            uploadedDocuments: remainingDocs,
            uploads: updatedUploads,
          };
        });
      },

      markAsUploaded: (documentId, fileName, fileUrl) => {
        set((state) => ({
          uploads: {
            ...state.uploads,
            [documentId]: {
              ...state.uploads[documentId],
              documentId,
              status: 'uploaded',
              fileName,
              fileUrl,
              uploadedAt: new Date().toISOString(),
            },
          },
        }));
      },

      markAsVerified: (documentId) => {
        set((state) => ({
          uploads: {
            ...state.uploads,
            [documentId]: {
              ...state.uploads[documentId],
              documentId,
              status: 'verified',
              verifiedAt: new Date().toISOString(),
            },
          },
        }));
      },

      markAsNotApplicable: (documentId) => {
        set((state) => ({
          uploads: {
            ...state.uploads,
            [documentId]: {
              documentId,
              status: 'not-applicable',
            },
          },
        }));
      },

      addNote: (documentId, note) => {
        set((state) => ({
          uploads: {
            ...state.uploads,
            [documentId]: {
              ...state.uploads[documentId],
              documentId,
              status: state.uploads[documentId]?.status || 'not-started',
              notes: note,
            },
          },
        }));
      },

      resetDocument: (documentId) => {
        set((state) => {
          const { [documentId]: _, ...rest } = state.uploads;
          return { uploads: rest };
        });
      },

      resetAll: () => {
        // Full reset to initial state — the sole caller is the sign-out cascade,
        // which must clear user-scoped selections (domain, stage, storage files)
        // too, not just the upload maps.
        set({ ...initialState });
      },

      setDomainDocumentStatus: (documentId, status, metadata) => {
        set((state) => ({
          domainUploads: {
            ...state.domainUploads,
            [documentId]: {
              ...state.domainUploads[documentId],
              ...metadata,
              documentId,
              status,
            },
          },
        }));
      },

      addStorageFile: (documentId, file) => {
        set((state) => ({
          storageFiles: {
            ...state.storageFiles,
            [documentId]: file,
          },
          uploads: {
            ...state.uploads,
            [documentId]: {
              ...state.uploads[documentId],
              documentId,
              status: 'uploaded',
              fileName: file.name,
              fileUrl: file.publicUrl || file.path,
              uploadedAt: file.createdAt,
            },
          },
        }));
      },

      removeStorageFile: (documentId) => {
        set((state) => {
          const { [documentId]: removed, ...rest } = state.storageFiles;
          const { [documentId]: removedUpload, ...restUploads } = state.uploads;
          return {
            storageFiles: rest,
            uploads: restUploads,
          };
        });
      },

      getStorageFile: (documentId) => {
        return get().storageFiles[documentId];
      },

      getUpload: (documentId) => {
        return get().uploads[documentId];
      },

      getUploadStatus: (documentId) => {
        return get().uploads[documentId]?.status || 'not-started';
      },

      isItemCovered: (documentId) => {
        const state = get();
        const status = state.uploads[documentId]?.status;
        if (status === 'uploaded' || status === 'verified' || status === 'not-applicable') {
          return true;
        }
        return state.uploadedDocuments.some((doc) => doc.coversItems.includes(documentId));
      },

      getCoveringDocument: (documentId) => {
        return get().uploadedDocuments.find((doc) => doc.coversItems.includes(documentId));
      },

      setLastSyncedAt: (iso) => set({ lastSyncedAt: iso }),
    }),
    {
      name: 'inverio-dataroom',
      storage: createJSONStorage(getStorage),
      partialize: (state) => ({
        uploads: state.uploads,
        uploadedDocuments: state.uploadedDocuments,
        currentStage: state.currentStage,
        domain: state.domain,
        domainUploads: state.domainUploads,
        storageFiles: state.storageFiles,
        lastSyncedAt: state.lastSyncedAt,
      }),
    }
  )
);
